// Pure pieces of the promises screen, its drawer and the CTP dialog (spec 03
// §4.9, D-463, D-474): the address, the filters it names, the basis as a
// checklist with each assumption's last verdict, and the refusals in words.

import type {
  CtpPromise,
  FulfillmentPath,
  PromiseBasis,
  PromiseBasisVerdict,
  PromiseFilters,
  PromiseStatus,
} from "../hooks/promise-queries";
import { PROMISE_STATUSES } from "../hooks/promise-queries";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const DATE = /^\d{4}-\d{2}-\d{2}$/u;

function uuid(v: unknown): string | undefined {
  return typeof v === "string" && UUID.test(v) ? v.toLowerCase() : undefined;
}

function date(v: unknown): string | undefined {
  if (typeof v !== "string" || !DATE.test(v)) return undefined;
  const t = Date.parse(`${v}T00:00:00Z`);
  if (Number.isNaN(t)) return undefined;
  return new Date(t).toISOString().slice(0, 10) === v ? v : undefined;
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

/**
 * The promises screen's address: its filters, the drawer (`promise`) and the
 * CTP dialog (`check`), which `line` binds to one order line so the dialog
 * can commit onto it (D-474; the order detail embeds the same dialog in J-2).
 */
export interface PromisesSearch {
  readonly status?: PromiseStatus | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly customer?: string | undefined;
  readonly product?: string | undefined;
  readonly order?: string | undefined;
  readonly promise?: string | undefined;
  readonly check?: "1" | undefined;
  readonly line?: string | undefined;
}

export function validatePromisesSearch(search: Record<string, unknown>): PromisesSearch {
  const out: Record<string, string> = {};
  const set = (key: string, value: string | undefined) => {
    if (value !== undefined) out[key] = value;
  };
  set("status", oneOf(search["status"], PROMISE_STATUSES));
  set("from", date(search["from"]));
  set("to", date(search["to"]));
  set("customer", uuid(search["customer"]));
  set("product", uuid(search["product"]));
  set("order", uuid(search["order"]));
  set("promise", uuid(search["promise"]));
  // TanStack's search parser reads `?check=1` as the number 1.
  const check = search["check"];
  if (check === "1" || check === 1 || check === true) out["check"] = "1";
  set("line", uuid(search["line"]));
  return out;
}

/** The address without the keys a change cleared. */
export function compactPromisesSearch(search: PromisesSearch): PromisesSearch {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined));
}

/** The keys of §5 row 77 the address filters by, in the order their chips show. */
export const PROMISE_FILTER_KEYS = [
  "status",
  "from",
  "to",
  "product",
  "customer",
  "order",
] as const;
export type PromiseFilterKey = (typeof PROMISE_FILTER_KEYS)[number];

export function promiseFiltersOf(search: PromisesSearch): PromiseFilters {
  const out: Record<string, string> = {};
  for (const key of PROMISE_FILTER_KEYS) {
    const value = search[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

export function activePromiseFilters(search: PromisesSearch): PromiseFilterKey[] {
  return PROMISE_FILTER_KEYS.filter((k) => search[k] !== undefined);
}

// ---- status and path ------------------------------------------------------------

export type ChipTone = "ok" | "warn" | "bad" | "neutral" | "accent";

/**
 * §4.9: quoted neutral, committed green, at risk amber, broken red; kept,
 * withdrawn and expired are muted (neutral without a dot).
 */
export function promiseStatusTone(status: PromiseStatus): ChipTone {
  switch (status) {
    case "committed":
      return "ok";
    case "at_risk":
      return "warn";
    case "broken":
      return "bad";
    default:
      return "neutral";
  }
}

/** A status that no longer moves: a muted chip and no Withdraw. */
export function isFinalPromise(status: PromiseStatus): boolean {
  return status === "kept" || status === "withdrawn" || status === "expired";
}

/**
 * Who may withdraw a promise (spec 03 §5 row 76, D-464 ruling 2): a quoted
 * promise by a holder of `planning.promise.commit`, and a committed or at-risk
 * one by a holder of `sales.order.re_promise` (from J-3), because that date is
 * what the customer was told. Kept, broken, withdrawn and expired promises
 * are past withdrawing (409 `PROMISE_NOT_WITHDRAWABLE`).
 */
export function canWithdrawPromise(
  promise: Pick<CtpPromise, "status">,
  canCommit: boolean,
  canRePromise: boolean,
): boolean {
  if (promise.status === "quoted") return canCommit;
  if (promise.status === "committed" || promise.status === "at_risk") return canRePromise;
  return false;
}

export const PATH_ORDER: readonly FulfillmentPath[] = ["offcut", "stock", "production", "transfer"];

// ---- the basis as a checklist -------------------------------------------------------

export type BasisItemKind =
  "offcut_unit" | "stock" | "in_transit" | "capacity_window" | "supply" | "material";

/** One assumption of a promise's basis, with its last verdict when the watch has checked it. */
export interface BasisItem {
  readonly key: string;
  readonly kind: BasisItemKind;
  /** The verdict's `ref` this assumption answers to. */
  readonly ref: string;
  /** True holds, false broken, null not checked yet. */
  readonly holds: boolean | null;
  readonly reasonKey: string | null;
  // What the item names, by kind.
  readonly lengthM?: string;
  readonly qty?: string;
  readonly workCenterId?: string;
  readonly windowStart?: string;
  readonly windowEnd?: string;
  readonly supplyKind?: "work_order" | "planned_order";
  readonly productId?: string;
  readonly source?: "on_hand" | "planned_order" | "lead_time";
  readonly availableDate?: string;
}

const ZERO = /^0+(?:\.0+)?$/u;

/** True for a decimal string that is zero. */
export function isZeroDecimal(qty: string): boolean {
  return ZERO.test(qty);
}

/** The verdict that answers one assumption: same kind, and the ref names it. */
function verdictFor(
  verdicts: readonly PromiseBasisVerdict[] | null,
  kind: PromiseBasisVerdict["kind"],
  refs: readonly string[],
): PromiseBasisVerdict | undefined {
  if (verdicts === null) return undefined;
  return verdicts.find(
    (v) => v.kind === kind && refs.some((r) => v.ref === r || v.ref.startsWith(`${r}@`)),
  );
}

/**
 * The basis as §4.9's checklist, in the order a reader checks a promise: the
 * off-cut units, stock on hand and in transit, the supply behind it, the
 * capacity windows, then the material each step waits for.
 */
export function basisItems(
  basis: PromiseBasis,
  verdicts: readonly PromiseBasisVerdict[] | null,
): BasisItem[] {
  const items: BasisItem[] = [];
  const judged = (
    kind: PromiseBasisVerdict["kind"],
    refs: readonly string[],
  ): Pick<BasisItem, "holds" | "reasonKey"> => {
    const v = verdictFor(verdicts, kind, refs);
    return { holds: v === undefined ? null : v.holds, reasonKey: v?.reason_key ?? null };
  };
  for (const unit of basis.atp.offcut_combination) {
    items.push({
      key: `offcut:${unit.stock_unit_id}`,
      kind: "offcut_unit",
      ref: unit.stock_unit_id,
      lengthM: unit.length_m,
      ...judged("offcut_unit", [unit.stock_unit_id]),
    });
  }
  if (!isZeroDecimal(basis.atp.on_hand_used)) {
    items.push({
      key: "stock",
      kind: "stock",
      ref: "on_hand",
      qty: basis.atp.on_hand_used,
      holds: null,
      reasonKey: null,
    });
  }
  if (!isZeroDecimal(basis.atp.in_transit_used)) {
    items.push({
      key: "in_transit",
      kind: "in_transit",
      ref: "in_transit",
      qty: basis.atp.in_transit_used,
      holds: null,
      reasonKey: null,
    });
  }
  if (basis.supply_ref !== null) {
    items.push({
      key: `supply:${basis.supply_ref.id}`,
      kind: "supply",
      ref: basis.supply_ref.id,
      supplyKind: basis.supply_ref.kind,
      ...judged("supply", [basis.supply_ref.id]),
    });
  }
  basis.capacity.forEach((w, i) => {
    items.push({
      key: `capacity:${w.work_center_id}:${w.window_start}:${String(i)}`,
      kind: "capacity_window",
      ref: w.work_center_id,
      workCenterId: w.work_center_id,
      windowStart: w.window_start,
      windowEnd: w.window_end,
      ...judged("capacity_window", [`${w.work_center_id}@${w.window_start}`, w.work_center_id]),
    });
  });
  basis.material_assumptions.forEach((m, i) => {
    const refs = m.ref_id !== null ? [m.ref_id, m.product_id] : [m.product_id];
    items.push({
      key: `material:${m.product_id}:${m.ref_id ?? m.source}:${String(i)}`,
      kind: "material",
      ref: m.ref_id ?? m.product_id,
      productId: m.product_id,
      source: m.source,
      availableDate: m.available_date,
      ...judged("material", refs),
    });
  });
  return items;
}

// ---- refusals in words --------------------------------------------------------------

/** The refusal codes rows 74 to 76 answer that have words of their own. */
export const PROMISE_REFUSALS = [
  "FACILITY_NOT_FOUND",
  "PRODUCT_NOT_FOUND",
  "ORDER_LINE_NOT_FOUND",
  "PRODUCT_ARCHIVED",
  "CAPACITY_PATH_UNAVAILABLE",
  "CTP_BUSY",
  "PROMISE_NOT_FOUND",
  "PROMISE_NOT_QUOTED",
  "PROMISE_EXPIRED",
  "ORDER_LINE_REQUIRED",
  "ORDER_LINE_NOT_PROMISABLE",
  "LINE_ALREADY_PROMISED",
  "PROMISE_NOT_WITHDRAWABLE",
  // Module 09's re-promise route (spec 03 §5 row 79, J-3).
  "ORDER_NOT_FOUND",
  "PERMISSION_DENIED",
] as const;
export type PromiseRefusal = (typeof PROMISE_REFUSALS)[number];

/** The i18n key of a refusal, or the generic one for a code with no words of its own. */
export function promiseRefusalKey(code: string | undefined): string {
  return code !== undefined && (PROMISE_REFUSALS as readonly string[]).includes(code)
    ? `planning.promises.refusal.${code}`
    : "planning.promises.refusal.other";
}

/** The reasons a 422 `CAPACITY_PATH_UNAVAILABLE` names (§3.7). */
export const NO_DATE_REASONS = [
  "no_routing",
  "no_bom",
  "facility_calendar_missing",
  "too_many_operations",
  "probe_unstable",
] as const;

/** Why an order line cannot take a promise (409 `ORDER_LINE_NOT_PROMISABLE`). */
export const LINE_REFUSAL_REASONS = [
  "order_not_confirmed",
  "credit_blocked",
  "line_cancelled",
  "line_shipped",
  "customer_mismatch",
  "product_mismatch",
  "facility_mismatch",
  "line_mismatch",
  "qty_short",
  "promise_kept",
] as const;

/** What made a commit's basis stale (409 `BASIS_STALE`). */
export const STALE_REASONS = ["date_moved", "unit_unavailable", "unplaced"] as const;

/** Keeps the known keys of a server list, in the server's order. */
export function knownKeys<T extends string>(raw: unknown, known: readonly T[]): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is T => typeof r === "string" && (known as readonly string[]).includes(r),
  );
}

// ---- the quantity box -----------------------------------------------------------------

const QTY_TEXT = /^\d{1,11}(?:\.\d{1,3})?$/u;

/**
 * A typed quantity as the contract's decimal (at most three places, above
 * zero), or undefined. A comma decimal separator reads as a point, and the
 * spaces a ru or kk keyboard groups digits with are dropped.
 */
export function parsePromiseQty(text: string): string | undefined {
  const raw = text.replace(/[\s\u00a0\u202f]/gu, "").replace(",", ".");
  if (!QTY_TEXT.test(raw)) return undefined;
  if (isZeroDecimal(raw)) return undefined;
  return raw;
}
